import { createReducer, on } from '@ngrx/store';
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { PhysicalAccessControlModels } from '@models/physical-access-control.models';
import { PhysicalAccessControlActions } from '@states/physical-access-control/physical-access-control.action-types';

export interface PhysicalAccessControlState
  extends EntityState<PhysicalAccessControlModels.PhysicalAccessControlDocument> {
  isInitialLoaded: boolean;
  isLoading: boolean;
  filters: {
    query: string;
  };
  isSaving: boolean;
  selectedPhysicalAccessControlDocument: PhysicalAccessControlModels.PhysicalAccessControlDocument;
  isFormValid: boolean;
  canDeactivate: boolean;
  testPassed: boolean;
  testLoading: boolean;
  testFinished: boolean;
  isDeleting: boolean;
}

export const adapter: EntityAdapter<PhysicalAccessControlModels.PhysicalAccessControlDocument> =
  createEntityAdapter<PhysicalAccessControlModels.PhysicalAccessControlDocument>({
    selectId: (document) => document._id,
  });

export const initialState: PhysicalAccessControlState = adapter.getInitialState({
  isInitialLoaded: false,
  isLoading: false,
  filters: {
    query: null,
  },
  isSaving: false,
  testPassed: false,
  testLoading: false,
  testFinished: false,
  selectedPhysicalAccessControlDocument: null,
  isFormValid: false,
  canDeactivate: false,
  isDeleting: false,
});

export const physicalAccessControlStateReducer = createReducer(
  initialState,
  on(PhysicalAccessControlActions.resetToInitialState, () => ({ ...initialState })),
  on(PhysicalAccessControlActions.getPhysicalAccessControl, (state) => ({ ...state, isLoading: true })),
  on(PhysicalAccessControlActions.getPhysicalAccessControlFail, (state) => ({
    ...state,
    isLoading: false,
    isInitialLoaded: false,
  })),
  on(PhysicalAccessControlActions.getPhysicalAccessControlSuccess, (state, { documents }) =>
    adapter.setMany(documents, { ...state, isLoading: false, isInitialLoaded: true }),
  ),
  on(PhysicalAccessControlActions.setSelectedPhysicalAccessControl, (state, { document }) => ({
    ...state,
    selectedPhysicalAccessControlDocument: document,
  })),
  on(PhysicalAccessControlActions.setQueryStringFilter, (state, { prop, value }) => ({
    ...state,
    isLoading: true,
    filters: { ...state.filters, [prop]: value },
  })),
  on(PhysicalAccessControlActions.setCanDeactivate, (state, { canDeactivate }) => ({ ...state, canDeactivate })),
  on(PhysicalAccessControlActions.resetEntities, (state) => ({ ...state, entities: initialState.entities })),
  on(PhysicalAccessControlActions.testPhysicalAccessControl, (state) => ({
    ...state,
    testPassed: false,
    testLoading: true,
    testFinished: false,
  })),
  on(PhysicalAccessControlActions.testPhysicalAccessControlSuccess, (state) => ({
    ...state,
    testPassed: true,
    testLoading: false,
    testFinished: true,
  })),
  on(PhysicalAccessControlActions.testPhysicalAccessControlError, (state) => ({
    ...state,
    testPassed: false,
    testLoading: false,
    testFinished: true,
  })),
  on(PhysicalAccessControlActions.setIsSaving, (state, { isSaving }) => ({ ...state, isSaving })),
  on(PhysicalAccessControlActions.deletePhysicalAccessControlSuccess, (state, { id }) =>
    adapter.removeOne(id, { ...state, isDeleting: false }),
  ),
  on(PhysicalAccessControlActions.getOnePhysicalAccessControlSuccess, (state, { document }) => ({
    ...state,
    selectedPhysicalAccessControlDocument: document,
    isLoading: false,
  })),
  on(PhysicalAccessControlActions.getOnePhysicalAccessControl, (state) => ({ ...state, isLoading: true })),
  on(PhysicalAccessControlActions.deletePhysicalAccessControlFail, (state) => ({ ...state, isDeleting: false })),
  on(PhysicalAccessControlActions.startDeletePhysicalAccessControl, (state) => ({ ...state, isDeleting: true })),
  on(PhysicalAccessControlActions.getOnePhysicalAccessControlFail, (state) => ({ ...state, isLoading: false })),
  on(PhysicalAccessControlActions.changeFormValidation, (state, { isFormValid }) => ({ ...state, isFormValid })),
);


